/**
 * Generation counter that lets an in-flight auth-session attempt be
 * superseded.
 *
 * `checkForActiveAuthSession` awaits network work — the Checkr Profile session
 * lookup and, behind the flag, the broker token exchange. That work can settle
 * after the attempt stopped being relevant: the route unmounted, another
 * route's bootstrap started a newer attempt, the end-to-end deadline expired,
 * or the candidate logged out. Without a generation check a late failure can
 * clear a newer session's token and a late success can restore state after
 * logout.
 *
 * Lives in its own module so `logoutCandidate` can invalidate attempts without
 * importing `candidateLogin` — which would close an import cycle through
 * `reports`.
 */
let generation = 0;

/**
 * The most recently *begun* attempt. Unlike `generation`, a plain
 * invalidation (unmount, deadline, logout) does not move it, so a superseded
 * attempt can tell "a newer attempt will settle the auth check" apart from
 * "nothing will, unless I do".
 */
let latestBegun = 0;

/** Start a new attempt, superseding any already in flight. */
export const beginAuthSessionAttempt = () => {
  generation += 1;
  latestBegun = generation;
  return generation;
};

/** True once a newer attempt, a deadline, or a logout has superseded this one. */
export const isStaleAuthSessionAttempt = attempt => attempt !== generation;

/** True once a newer attempt has been begun, which will settle the auth check itself. */
export const hasNewerAuthSessionAttempt = attempt => latestBegun !== attempt;

/** Supersede any in-flight attempt without starting a new one. */
export const invalidateAuthSessionAttempts = () => {
  generation += 1;
};
